import { useEffect, useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Button } from "@/components/ui/button";
import { Bell } from "lucide-react";

export default function NotificationsBell() {
  const [items, setItems] = useState([]);
  const [unread, setUnread] = useState(0);
  const navigate = useNavigate();
  const timer = useRef(null);

  const fetchData = async () => {
    try {
      const [{ data: list }, { data: u }] = await Promise.all([
        api.get("/notifications"),
        api.get("/notifications/unread-count"),
      ]);
      setItems(list);
      setUnread(u.count);
    } catch (e) {
      // 401 just means not logged in — quietly skip. Anything else is worth logging.
      if (e?.response?.status && e.response.status !== 401) {
        console.warn("Notifications poll failed", e.message);
      }
    }
  };

  useEffect(() => {
    fetchData();
    timer.current = setInterval(fetchData, 30000);
    return () => clearInterval(timer.current);
  }, []);

  const open = async (n) => {
    if (!n.read) {
      await api.post(`/notifications/${n.id}/read`);
      fetchData();
    }
    if (n.link) navigate(n.link);
  };

  const markAll = async () => {
    await api.post("/notifications/read-all");
    fetchData();
  };

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant="ghost" size="icon" className="relative rounded-[35px] shadow-sm hover:shadow-md" data-testid="notifications-bell">
          <Bell size={18} />
          {unread > 0 && (
            <span className="absolute -top-1 -right-1 bg-[#B31E1E] text-white text-[10px] font-mono px-1.5 py-0.5 rounded-sm" data-testid="notifications-unread-count">
              {unread}
            </span>
          )}
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent className="w-80 rounded-sm" align="end">
        <div className="flex items-center justify-between px-3 py-2 border-b border-border">
          <span className="overline text-gray-500">Notifications</span>
          {unread > 0 && (
            <button onClick={markAll} className="text-xs text-[#B31E1E] hover:underline" data-testid="notifications-mark-all">
              Mark all read
            </button>
          )}
        </div>
        <div className="max-h-80 overflow-y-auto">
          {items.length === 0 && (
            <p className="text-sm text-gray-500 p-4">You're all caught up.</p>
          )}
          {items.map((n) => (
            <button
              key={n.id}
              onClick={() => open(n)}
              className={`w-full text-left px-3 py-3 border-b border-border hover:bg-gray-50 transition-colors ${!n.read ? "bg-blue-50/40" : ""}`}
              data-testid={`notification-${n.id}`}
            >
              <p className="text-sm">{n.message}</p>
              <p className="text-[10px] font-mono text-gray-400 mt-1">
                {new Date(n.created_at).toLocaleString()}
              </p>
            </button>
          ))}
        </div>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
